import './SingleSkuDetail.less';
import { Affix, PageHeader, Divider, Tabs } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from 'components/Icons';
import { Headline } from 'components/RevxTypography';
import React, { useState } from 'react';
import { SkuTable } from './SkuTable';
import { error, success } from 'components/RevxMessage';
import { RevxSpin } from 'components/RevxSpin';
import { ADMIN_DETAIL_AFFIX_TOP } from 'utils/constants';
import { useOrg, useOrgId } from 'hooks/useOrg';
import { isPlatformOrg } from 'domains/orgDomain';
import { PagerStateProp } from 'models/skuModel';

const { TabPane } = Tabs;

const SkuCreateWithInheritanceView = () => {
  const { t } = useTranslation();

  const curItem = useAppSelector((state) => state.sku.curItem);
  const dispatch = useAppDispatch();
  const pagedData = useAppSelector((state) => state.sku.pagedData);
  const [inheritFromSkuId, setInheritFromSkuId] = useState<React.Key>();
  const isSaving = useAppSelector((state) => state.loading.effects.sku.saveSku);
  const org = useOrg();
  const orgId = useOrgId();

  const onClose = () => {
    dispatch.sku.resetInheritTablePager();
  };

  const onSave = async () => {
    if (inheritFromSkuId === undefined) {
      error('Please select SKU you want to inherit from');
      return;
    } else {
      const sku = {
        ...pagedData?.content.find((sku) => sku.id === inheritFromSkuId),
        inheritedFromId: inheritFromSkuId as number,
      };

      await dispatch.sku.saveSkuWithInheritance(sku);

      dispatch.sku.refresh();

      success(t('platform.sku.createSuccess'));
      onClose();
      // TODO, call model service to create with inheritance
    }
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>{t('platform.sku.createSkuWithInheritance')}</Headline>
          }
          extra={[
            <RevxButton
              type="primary"
              htmlType="submit"
              brandGreen
              onClick={onSave}
            >
              {t('common.save')}
            </RevxButton>,
            <>
              <Divider type="vertical" />
              <RevxButton onClick={onClose} brandGreen>
                {t('common.cancel')}
              </RevxButton>
            </>,
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );

  const [tabKey, setTabKey] = useState('base');

  return (
    <RevxSpin spinning={isSaving}>
      <div className="single-sku-detail" hidden={!!curItem}>
        {actionBar}
        <div style={{ padding: '0 24px' }}>
          <Headline>{t('platform.sku.selectSkuInheritTip')}</Headline>

          {isPlatformOrg({ id: orgId }) ? (
            <SkuTable
              isInherit
              pagerStateProp="inheritBasePager"
              tenantId={1}
              selectedSkuId={inheritFromSkuId}
              onSelectedChange={(val) => setInheritFromSkuId(val)}
            />
          ) : (
            <Tabs
              activeKey={tabKey}
              onChange={(tab) => {
                const pagerMap: { [index: string]: PagerStateProp } = {
                  base: 'inheritBasePager',
                  parent: 'inheritParentPager',
                  current: 'inheritCurrentPager',
                };

                setTabKey(tab);
                dispatch.sku.setState({
                  pagerStateProp: pagerMap[tab],
                });
              }}
            >
              <TabPane tab={t('platform.sku.baseSkus')} key="base">
                {tabKey === 'base' && (
                  <SkuTable
                    isInherit
                    pagerStateProp="inheritBasePager"
                    tenantId={1}
                    selectedSkuId={inheritFromSkuId}
                    onSelectedChange={(val) => setInheritFromSkuId(val)}
                  />
                )}
              </TabPane>
              {org?.parentId && (
                <TabPane tab={t('platform.sku.parentOrgSkus')} key="parent">
                  {tabKey === 'parent' && (
                    <SkuTable
                      isInherit
                      pagerStateProp="inheritParentPager"
                      tenantId={org.parentId}
                      selectedSkuId={inheritFromSkuId}
                      onSelectedChange={(val) => setInheritFromSkuId(val)}
                    />
                  )}
                </TabPane>
              )}
              <TabPane tab={t('platform.sku.currentOrgSkus')} key="current">
                {tabKey === 'current' && (
                  <SkuTable
                    isInherit
                    pagerStateProp="inheritCurrentPager"
                    selectedSkuId={inheritFromSkuId}
                    onSelectedChange={(val) => setInheritFromSkuId(val)}
                  />
                )}
              </TabPane>
            </Tabs>
          )}
        </div>
      </div>
    </RevxSpin>
  );
};
export default SkuCreateWithInheritanceView;
